import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../core/helpers/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-70",
  {
    variants: {
      variant: {
        default: "bg-accent-9 text-white hover:bg-accent-9/90",
        error: "bg-error- text-error- hover:bg-error-/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground text-neutral-11",
        link: "text-accent-2 underline-offset-4 hover:underline",
        surface:
          "border border-mode-foreground border-opacity-alpha7 text-neutral-11",
        solid: "bg-accent-12 text-neutral-1",
        amber: "bg-amber-9 text-black hover:bg-amber-9/90",
        neutralsolid: "bg-neutral-12 text-neutral-1",
        gray: "bg-neutral-alpha-3 text-neutral-12",
        accentb:
          "border border-accent-secondary-alpha-7 bg-accent-secondary-alpha-2 text-accent-secondary-alpha-11 hover:opacity-80",
        amberb:
          "border border-accent-alpha-7 bg-accent-alpha-2 text-amber-11 hover:opacity-80",
      },
      size: {
        default: "h-10 px-4 py-2",
        1: "h-6 px-2 py-1 text-xs font-medium",
        2: "h-8 px-3 py-1 text-sm font-medium",
        3: "h-10 px-4 py-2 text-md font-medium",
        4: "h-12 px-6 py-3 text-lg font-medium",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "amber",
      size: 4,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
